<template>
  <ElDialog
      v-model="visible"
      :footer="null"
      append-to-body
      align-center
      :show-close="true"
      :close-on-click-modal="false"
      :width="720"
      top="4em"
      destroyOnClose
  >
    <template #header>
      <span class="el-dialog__title">{{ t('resource_detail.title') }}</span>
      <div class="dialog-header-divider"></div>
    </template>
    <div class="resource-detail-content" v-loading="loading">
      <ElTable
          :data="pagedData"
          border
          style="width: 100%"
          :cell-style="cellStyle"
          :header-cell-style="headerCellStyle"
      >
        <ElTableColumn prop="time" :label="t('resource_detail.time')" min-width="200" />
        <ElTableColumn prop="resource" :label="t('resource_detail.resource')" min-width="320">
          <template #default="{ row }">
            <span>{{ getResourceLabelParts(row).prefix }}</span>
            <strong v-if="getResourceLabelParts(row).qty">{{ getResourceLabelParts(row).qty }}</strong>
          </template>
        </ElTableColumn>
      </ElTable>

      <div class="pages" v-if="totalCount > pageSize">
        <ElPagination
            background
            layout="prev, pager, next, sizes, total"
            :total="totalCount"
            :current-page="currentPage"
            :page-size="pageSize"
            :page-sizes="pageSizes"
            @update:current-page="handleCurrentPageChange"
            @update:page-size="handlePageSizeChange"
        />
      </div>
    </div>
  </ElDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import {ElDialog, ElMessage, ElPagination, ElTable, ElTableColumn} from 'element-plus'
import {useI18n} from 'vue-i18n'
import {getInviteList} from '@/api/index'

const { t } = useI18n()

const getResourceLabelKey = (row) => {
  const raw = String(row?.resource ?? '')
  if (raw.includes('工位')) return 'seat'
  if (raw.includes('存储') || raw.includes('空间') || raw.includes('GB')) return 'storage'
  if (raw.includes('邮件') || raw.includes('EDM')) return 'email'
  if (raw.includes('点数') || raw.toLowerCase().includes('ai')) return 'ai_points'
  return ''
}

const getResourceLabelParts = (row) => {
  const key = getResourceLabelKey(row)
  if (!key) {
    const raw = String(row?.resource ?? '')
    return {prefix: raw, qty: ''}
  }
  return {
    prefix: t(`invite_friend.resource_label.${key}_prefix`),
    qty: t(`invite_friend.resource_label.${key}_qty`),
  }
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  useInviteList: {
    type: Boolean,
    default: false
  },
  data: {
    type: Array,
    default: () => []
  },
  page: {
    type: Number,
    default: 1
  },
  pageSize: {
    type: Number,
    default: 10
  },
  pageSizes: {
    type: Array,
    default: () => [10, 20, 50, 100]
  },
  total: {
    type: Number,
    default: 0
  },
  serverSide: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'update:page', 'update:pageSize', 'page-change'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const innerPage = ref(props.page)

const loading = ref(false)
const serverRows = ref([])
const serverTotal = ref(0)
const serverPage = ref(props.page)
const serverPageSize = ref(props.pageSize)

const currentPage = computed(() => (props.useInviteList ? serverPage.value : props.page))

const pageSize = computed(() => (props.useInviteList ? serverPageSize.value : props.pageSize))

const totalCount = computed(() => {
  if (props.useInviteList) return serverTotal.value || 0
  if (props.serverSide) return props.total || 0
  return Array.isArray(props.data) ? props.data.length : 0
})

const pagedData = computed(() => {
  if (props.useInviteList) return serverRows.value
  if (props.serverSide) return props.data
  const list = Array.isArray(props.data) ? props.data : []
  const start = (currentPage.value - 1) * pageSize.value
  return list.slice(start, start + pageSize.value)
})

const normalizeRow = (row) => {
  const time = row?.createTime
  const resource = row?.productRemark
  return {
    ...row,
    time,
    resource
  }
}

const fetchInvitePage = async () => {
  if (!props.useInviteList) return
  loading.value = true
  try {
    const res = await getInviteList({
      pageNo: serverPage.value,
      pageSize: serverPageSize.value
    })
    if (res?.code !== 200) {
      ElMessage.error(res?.message || t('resource_detail.fetch_failed'))
      return
    }
    const result = res?.result || {}
    const records = result?.records || result?.list || []
    serverRows.value = (Array.isArray(records) ? records : []).map(normalizeRow)
    serverTotal.value = Number(result?.total ?? 0)
    serverPage.value = Number(result?.current ?? serverPage.value)
    serverPageSize.value = Number(result?.size ?? serverPageSize.value)
  } catch (e) {
    ElMessage.error(t('resource_detail.fetch_failed'))
  } finally {
    loading.value = false
  }
}

watch(
  () => visible.value,
  (val) => {
    if (!val) return
    if (!props.useInviteList) return
    serverPage.value = 1
    fetchInvitePage()
  }
)

const cellStyle = ({ columnIndex }) => {
  if (columnIndex === 0 || columnIndex === 1) {
    return { textAlign: 'left' }
  }
  return { textAlign: 'center' }
}

const headerCellStyle = ({ columnIndex }) => {
  if (columnIndex === 0 || columnIndex === 1) {
    return { 'text-align': 'left', background: '#f9f9f9', color: 'black', padding: '8px 0px' }
  }
  return { 'text-align': 'center', background: '#f9f9f9', color: 'black', padding: '8px 0px' }
}

const handleCurrentPageChange = (val) => {
  innerPage.value = val
  if (props.useInviteList) {
    serverPage.value = val
    fetchInvitePage()
    return
  }
  emit('update:page', val)
  emit('page-change', { page: val, pageSize: props.pageSize })
}

const handlePageSizeChange = (val) => {
  if (props.useInviteList) {
    serverPageSize.value = val
    serverPage.value = 1
    fetchInvitePage()
    return
  }
  emit('update:pageSize', val)
  emit('page-change', { page: currentPage.value, pageSize: val })
}
</script>

<style scoped lang="less">
.resource-detail-content {
  padding: 0 10px;
}

.dialog-header-divider {
  border-bottom: 1px solid #f3f3f3;
  margin-top: 10px;
  width: 100%;
}

.pages {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
